<script setup lang="ts">
import { computed } from "vue";
import AppIcon from "./AppIcon.vue";

const props = withDefaults(
  defineProps<{
    variant?: "primary" | "secondary" | "ghost" | "danger" | "success";
    size?: "sm" | "md";
    icon?: string;
    loading?: boolean;
    disabled?: boolean;
    type?: "button" | "submit";
  }>(),
  { variant: "secondary", size: "md", type: "button", icon: "" },
);

const base =
  "inline-flex items-center justify-center gap-1.5 rounded-[10px] font-medium " +
  "transition-[transform,background-color,box-shadow,opacity] duration-200 ease-[var(--ease-apple)] " +
  "active:scale-[0.97] disabled:opacity-45 disabled:pointer-events-none select-none " +
  "focus-visible:outline-none focus-visible:ring-[3.5px] focus-visible:ring-accent-soft";

const variants: Record<string, string> = {
  primary: "bg-accent text-accent-contrast shadow-soft hover:bg-accent-hover",
  secondary:
    "bg-surface text-content border border-separator-strong shadow-soft hover:bg-surface-hover",
  ghost: "text-secondary hover:text-content hover:bg-surface-hover",
  danger: "bg-bad-soft text-bad hover:brightness-105 border border-transparent",
  success: "bg-ok text-ok-contrast shadow-soft hover:brightness-110",
};

const sizes: Record<string, string> = {
  sm: "h-8 px-2.5 text-[13px]",
  md: "h-9.5 px-3.5 text-[13.5px]",
};

const classes = computed(() => [base, variants[props.variant], sizes[props.size]].join(" "));
</script>

<template>
  <button :type="type" :class="classes" :disabled="disabled || loading">
    <span
      v-if="loading"
      class="animate-spin-slow size-3.5 rounded-full border-[1.8px] border-current border-t-transparent"
    />
    <AppIcon v-else-if="icon" :name="icon" :size="size === 'sm' ? 14 : 16" />
    <slot />
  </button>
</template>
